import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ApiService } from './api.service';
import { jobStatusClass } from './job-status-style';

interface FleetRow {
  name: string;
  status: string;
  job: string;
  address: string;
  duration: string;
  action: string;
  route: any[];
}

interface AttentionItem {
  label: string;
  detail: string;
  tone: 'danger' | 'warning' | 'info';
  route: string;
  action?: string;
}

interface QuotePipelineMetric {
  label: string;
  amount: number;
  detail: string;
  tone: 'success' | 'warning' | 'info' | 'danger';
  progress: number;
  route: string;
}

interface FeedbackSummaryItem {
  id: number;
  reference_id: string;
  model_type: string;
  customer_name: string;
  rating: number | null;
  comments: string;
  status: string;
  testimonial_permission: any;
  submitted_at: string;
  requested_at: string;
  customer_id: number | null;
}

interface LocationSummaryItem {
  user_id: number;
  worker_name: string;
  latitude: string | number | null;
  longitude: string | number | null;
  location_updated_at: string;
  rego: string;
  vehicle_name: string;
  current_job: string;
  vehicle_location: string;
  active_count?: number;
  missing_count?: number;
  fleet_count?: number;
}

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="p-4 md:p-5 min-h-full bg-[#eef3f8]">
      <div class="flex flex-col xl:flex-row xl:items-end justify-between gap-3 mb-4">
        <div class="min-w-0">
          <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Auckland Operations</p>
          <h1 class="text-xl font-bold text-slate-950 tracking-tight leading-tight">Command Center</h1>
          <p class="text-slate-500 text-[11px]">Live overview for dispatch, field work, and commercial follow-up.</p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button type="button" (click)="refreshDashboard()" class="h-8 px-3 rounded-md bg-white border border-slate-200 text-slate-600 text-[11px] font-semibold hover:bg-slate-50 transition-colors flex items-center gap-2">
            <i class="ti ti-refresh text-[13px]"></i> Refresh
          </button>
          <div class="h-8 inline-flex rounded-md bg-white border border-slate-200 p-0.5">
            <button type="button" (click)="setDashboardScope('Today')" class="h-7 px-3 rounded text-[11px] font-semibold flex items-center gap-2 transition-colors" [class]="dashboardScope() === 'Today' ? 'bg-[#EAF5FA] text-[#1A6FA6]' : 'text-slate-500 hover:text-slate-900'">
              <i class="ti ti-calendar text-[13px]"></i> Today
            </button>
            <button type="button" (click)="setDashboardScope('All')" class="h-7 px-3 rounded text-[11px] font-semibold transition-colors" [class]="dashboardScope() === 'All' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-900'">
              All
            </button>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3 mb-4">
        <a routerLink="/admin/dispatch/control" class="bg-white rounded-lg border border-[#E7EEF5] px-5 py-4 hover:border-[#BEE7F9] transition-colors group">
          <div class="min-w-0">
            <p class="text-[10px] font-semibold text-slate-400">Assigned plumbers</p>
            <p class="text-2xl font-semibold text-slate-950 leading-none mt-2">{{ activePlumbersCount() }}</p>
            <p class="text-[10px] text-emerald-600 mt-3">{{ totalPlumbers() }} active plumber{{ totalPlumbers() === 1 ? '' : 's' }} available</p>
          </div>
        </a>

        <a routerLink="/admin/dispatch/unassigned" class="bg-[#fff6f3] rounded-lg border border-orange-200 px-5 py-4 hover:border-orange-300 transition-colors">
          <div class="min-w-0">
            <p class="text-[10px] font-semibold text-orange-700">Unassigned jobs</p>
            <p class="text-2xl font-semibold text-orange-600 leading-none mt-2">{{ unassignedJobsCount() }}</p>
            <p class="text-[10px] text-orange-600 mt-3">{{ urgentJobsCount() }} urgent unassigned</p>
          </div>
        </a>

        <a routerLink="/admin/financials/quotes" class="bg-white rounded-lg border border-[#E7EEF5] px-5 py-4 hover:border-[#BEE7F9] transition-colors">
          <div class="min-w-0">
            <p class="text-[10px] font-semibold text-slate-400">Pending quotes</p>
            <p class="text-2xl font-semibold text-slate-950 leading-none mt-2">{{ pendingQuotesCount() }}</p>
            <p class="text-[10px] text-amber-600 mt-3">{{ pendingQuotesCount() }} awaiting action</p>
          </div>
        </a>

        <a routerLink="/admin/dispatch/allocated" [queryParams]="{ status: 'Quote Sent' }" class="bg-cyan-50/50 rounded-lg border border-cyan-200 px-5 py-4 hover:border-cyan-300 transition-colors">
          <div class="min-w-0">
            <p class="text-[10px] font-semibold text-cyan-700">Quote sent jobs</p>
            <p class="text-2xl font-semibold text-cyan-700 leading-none mt-2">{{ quoteSentJobsCount() }}</p>
            <p class="text-[10px] text-cyan-600 mt-3">Awaiting customer response</p>
          </div>
        </a>

        <a routerLink="/admin/dispatch/ready" class="bg-white rounded-lg border border-[#E7EEF5] px-5 py-4 hover:border-emerald-200 transition-colors">
          <div class="min-w-0">
            <p class="text-[10px] font-semibold text-slate-400">Ready for office</p>
            <p class="text-2xl font-semibold text-slate-950 leading-none mt-2">{{ readyForOfficeCount() }}</p>
            <p class="text-[10px] text-emerald-600 mt-3">{{ readyForOfficeCount() }} ready</p>
          </div>
        </a>

        <a routerLink="/admin/operations/fleet" class="bg-white rounded-lg border border-[#E7EEF5] px-5 py-4 hover:border-rose-200 transition-colors">
          <div class="min-w-0">
            <p class="text-[10px] font-semibold text-slate-400">Fleet alerts</p>
            <p class="text-2xl font-semibold text-slate-950 leading-none mt-2">{{ fleetAlertCount() }}</p>
            <p class="text-[10px] text-orange-600 mt-3">{{ fleetAlertCount() ? 'Check fleet' : 'Fleet clear' }}</p>
          </div>
        </a>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <a routerLink="/admin/customer-feedback" class="bg-white rounded-lg border border-[#E7EEF5] px-5 py-4 hover:border-rose-200 transition-colors">
          <p class="text-[10px] font-semibold text-slate-400">Feedback follow-up</p>
          <p class="text-2xl font-semibold text-slate-950 leading-none mt-2">{{ feedbackNeedsFollowUpCount() }}</p>
          <p class="text-[10px] text-rose-600 mt-3">{{ feedbackNeedsFollowUpCount() ? 'Customer recovery needed' : 'No low feedback' }}</p>
        </a>

        <a routerLink="/admin/customer-feedback" class="bg-white rounded-lg border border-[#E7EEF5] px-5 py-4 hover:border-emerald-200 transition-colors">
          <p class="text-[10px] font-semibold text-slate-400">New feedback</p>
          <p class="text-2xl font-semibold text-slate-950 leading-none mt-2">{{ newFeedbackCount() }}</p>
          <p class="text-[10px] text-emerald-600 mt-3">{{ testimonialCandidateCount() }} testimonial candidate{{ testimonialCandidateCount() === 1 ? '' : 's' }}</p>
        </a>

        <a routerLink="/admin/operations/workers-control" class="bg-white rounded-lg border border-[#E7EEF5] px-5 py-4 hover:border-[#BEE7F9] transition-colors">
          <p class="text-[10px] font-semibold text-slate-400">Workers control</p>
          <p class="text-2xl font-semibold text-slate-950 leading-none mt-2">{{ locatedWorkerCount() }}</p>
          <p class="text-[10px] text-[#1A6FA6] mt-3">{{ missingLocationCount() ? missingLocationCount() + ' missing GPS' : activeLocationCount() + ' active shift rows' }}</p>
        </a>

        <a routerLink="/admin/operations/workers-control" class="bg-white rounded-lg border border-[#E7EEF5] px-5 py-4 hover:border-amber-200 transition-colors">
          <p class="text-[10px] font-semibold text-slate-400">Fleet on map</p>
          <p class="text-2xl font-semibold text-slate-950 leading-none mt-2">{{ fleetLocationCount() }}</p>
          <p class="text-[10px] text-amber-600 mt-3">{{ activeLocationCount() ? 'From active plumber shifts' : 'No active shifts' }}</p>
        </a>
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-12 gap-3">
        <div class="xl:col-span-6 bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 class="text-[12px] font-bold text-slate-900">Live fleet and field status</h2>
              <p class="text-[10px] text-slate-500 mt-0.5">Real-time in-progress work across assigned field staff.</p>
            </div>
            <span class="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-[10px] font-semibold text-emerald-700 w-fit">{{ fleet().length }} active</span>
          </div>

          @if (fleet().length > 0) {
            <div class="overflow-x-auto">
              <table class="w-full min-w-[560px] text-left text-[11px]">
                <thead>
                  <tr class="border-b border-slate-100">
                    <th class="w-[24%] px-4 py-3 text-[9px] font-semibold text-slate-400">Plumber</th>
                    <th class="w-[34%] px-3 py-3 text-[9px] font-semibold text-slate-400">Job and location</th>
                    <th class="w-[18%] px-3 py-3 text-[9px] font-semibold text-slate-400">Status</th>
                    <th class="w-[14%] px-3 py-3 text-[9px] font-semibold text-slate-400">Duration</th>
                    <th class="w-[10%] px-4 py-3 text-[9px] font-semibold text-slate-400 text-right">Action</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  @for (van of fleet(); track van.name + van.job) {
                    <tr class="hover:bg-slate-50 transition-colors">
                      <td class="px-4 py-3">
                        <div class="flex items-center gap-2">
                          <div class="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold bg-sky-50 text-[#1A6FA6] shrink-0">{{ van.name.charAt(0) }}</div>
                          <span class="font-semibold text-slate-800 truncate">{{ van.name }}</span>
                        </div>
                      </td>
                      <td class="px-3 py-3">
                        <div class="min-w-0">
                          <a [routerLink]="van.route" class="font-semibold text-slate-900 hover:text-[#1A6FA6]">{{ van.job }}</a>
                          <p class="text-[10px] text-slate-400 truncate">{{ van.address || 'No site address' }}</p>
                        </div>
                      </td>
                      <td class="px-3 py-3">
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border" [ngClass]="statusBadgeClass(van.status)">
                          <span class="w-1.5 h-1.5 rounded-full bg-current"></span>{{ van.status }}
                        </span>
                      </td>
                      <td class="px-3 py-3 text-slate-600">
                        <div>
                          <p class="font-semibold">{{ van.duration }}</p>
                          <div class="mt-1 h-0.5 w-10 rounded-full bg-slate-100 overflow-hidden"><div class="h-full w-2/3 bg-emerald-500"></div></div>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-right">
                        <a [routerLink]="van.route" class="inline-flex h-7 px-3 rounded-md bg-sky-50 text-[#1A6FA6] text-[10px] font-bold items-center justify-center hover:bg-sky-100 whitespace-nowrap">{{ van.action }}</a>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <div class="px-4 py-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
              <span>{{ activePlumbersCount() }} active - {{ urgentJobsCount() }} urgent - {{ readyForOfficeCount() }} ready</span>
              <a routerLink="/admin/dispatch/control" class="h-6 px-3 rounded-md bg-sky-50 text-[#1A6FA6] font-bold">Jobs Control</a>
            </div>
          } @else {
            <div class="p-10 text-center">
              <div class="w-12 h-12 rounded-lg bg-slate-50 border border-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
                <i class="ti ti-truck-off text-2xl"></i>
              </div>
              <p class="text-sm font-semibold text-slate-900">No active field rows</p>
              <p class="text-xs text-slate-500 mt-1">In-progress assignments will appear here.</p>
            </div>
          }
        </div>

        <div class="xl:col-span-3 bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
            <div>
              <h2 class="text-[12px] font-bold text-slate-900">Attention queue</h2>
              <p class="text-[10px] text-slate-500 mt-0.5">Items needing office action now.</p>
            </div>
            <span class="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-[10px] font-semibold text-amber-700">{{ attentionItems().length }} items</span>
          </div>
          <div class="p-4 space-y-3">
            @for (item of attentionItems().slice(0, 5); track item.label) {
              <a [routerLink]="item.route" class="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-slate-50 transition-colors">
                <span class="w-7 h-7 rounded-md shrink-0 flex items-center justify-center" [ngClass]="attentionIconClass(item.tone)">
                  <i class="ti text-[15px]" [class]="'ti-' + attentionIconName(item)"></i>
                </span>
                <div class="min-w-0 flex-1">
                  <p class="text-[12px] font-bold text-slate-900 truncate">{{ item.label }}</p>
                  <p class="text-[10px] text-slate-500 mt-0.5 truncate">{{ item.detail }}</p>
                </div>
                <span class="h-7 min-w-[58px] px-2.5 rounded-md text-[10px] font-bold shrink-0 flex items-center justify-center" [ngClass]="attentionActionClass(item.tone)">{{ item.action || 'View' }}</span>
              </a>
            }
            @if (attentionItems().length === 0) {
              <div class="rounded-lg border border-slate-100 bg-slate-50 px-4 py-8 text-center">
                <p class="text-sm font-semibold text-slate-900">No priority items</p>
                <p class="text-xs text-slate-500 mt-1">The board is quiet right now.</p>
              </div>
            }
          </div>
        </div>

        <div class="xl:col-span-3 bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
            <div>
              <h2 class="text-[12px] font-bold text-slate-900">Quote pipeline</h2>
              <p class="text-[10px] text-slate-500 mt-0.5">Drafts, sent quotes, and approvals.</p>
            </div>
            <span class="px-2 py-0.5 rounded-full bg-sky-50 text-[#1A6FA6] text-[10px] font-bold">Live</span>
          </div>
          <div class="p-4 space-y-4">
            @for (metric of quotePipelineMetrics(); track metric.label) {
              <a [routerLink]="metric.route" class="block">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-[12px] font-bold text-slate-900">{{ metric.label }}</p>
                    <p class="text-[10px] text-slate-500 mt-0.5">{{ metric.detail }}</p>
                  </div>
                  <p class="text-[12px] font-bold" [ngClass]="quoteToneClass(metric.tone)">{{ metric.amount | currency:'NZD':'symbol':'1.0-0' }}</p>
                </div>
                <div class="mt-2 h-1 rounded-full bg-slate-100 overflow-hidden">
                  <div class="h-full rounded-full" [style.width.%]="metric.progress" [ngClass]="quoteBarClass(metric.tone)"></div>
                </div>
              </a>
            }
            <div class="pt-3 border-t border-slate-100 flex items-center justify-between">
              <p class="text-[10px] text-slate-400">Open quote value</p>
              <p class="text-[13px] font-bold text-[#1A6FA6]">{{ openQuoteValue() | currency:'NZD':'symbol':'1.0-0' }}</p>
            </div>
            <a routerLink="/admin/financials/quotes" class="h-8 rounded-md border border-slate-200 bg-slate-50 text-slate-500 text-[10px] font-semibold flex items-center justify-center hover:bg-white">
              Full quotes view
            </a>
          </div>
        </div>

        <div class="xl:col-span-6 bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
            <div>
              <h2 class="text-[12px] font-bold text-slate-900">Customer feedback</h2>
              <p class="text-[10px] text-slate-500 mt-0.5">Recent service ratings and customer recovery items.</p>
            </div>
            <span class="px-2 py-0.5 rounded-full bg-rose-50 border border-rose-100 text-[10px] font-semibold text-rose-700">{{ feedbackNeedsFollowUpCount() }} follow-up</span>
          </div>
          <div class="p-4">
            @if (feedbackItems().length > 0) {
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div class="rounded-md border border-slate-100 bg-slate-50 px-3 py-3">
                  <p class="text-[10px] font-semibold text-slate-500">Average rating</p>
                  <p class="text-lg font-bold text-slate-950 mt-1">{{ averageFeedbackRating() }}</p>
                </div>
                <div class="rounded-md border border-slate-100 bg-slate-50 px-3 py-3">
                  <p class="text-[10px] font-semibold text-slate-500">Submitted</p>
                  <p class="text-lg font-bold text-slate-950 mt-1">{{ submittedFeedbackCount() }}</p>
                </div>
                <div class="rounded-md border border-slate-100 bg-slate-50 px-3 py-3">
                  <p class="text-[10px] font-semibold text-slate-500">Testimonials</p>
                  <p class="text-lg font-bold text-slate-950 mt-1">{{ testimonialCandidateCount() }}</p>
                </div>
              </div>
              <div class="space-y-2">
                @for (item of feedbackItems().slice(0, 4); track item.id) {
                  <a [routerLink]="feedbackRoute(item)" class="flex items-center gap-3 rounded-md border border-slate-100 px-3 py-2.5 hover:bg-slate-50 transition-colors">
                    <span class="w-8 h-8 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0" [ngClass]="feedbackRatingClass(item)">
                      {{ item.rating || '-' }}
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="text-[12px] font-bold text-slate-900 truncate">{{ item.customer_name || item.reference_id || 'Customer feedback' }}</p>
                      <p class="text-[10px] text-slate-500 mt-0.5 truncate">{{ shortText(item.comments || item.status || 'Feedback request sent') }}</p>
                    </div>
                    <span class="h-7 px-2.5 rounded-md text-[10px] font-bold shrink-0" [ngClass]="feedbackStatusClass(item.status)">{{ item.status || 'Requested' }}</span>
                  </a>
                }
              </div>
            } @else {
              <div class="rounded-lg border border-slate-100 bg-slate-50 px-4 py-8 text-center">
                <p class="text-sm font-semibold text-slate-900">No feedback yet</p>
                <p class="text-xs text-slate-500 mt-1">Completed job feedback will appear here.</p>
              </div>
            }
          </div>
        </div>

        <div class="xl:col-span-6 bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
            <div>
              <h2 class="text-[12px] font-bold text-slate-900">Live location coverage</h2>
              <p class="text-[10px] text-slate-500 mt-0.5">Active shifts, latest GPS updates, and assigned fleet visibility.</p>
            </div>
            <span class="px-2 py-0.5 rounded-full bg-sky-50 border border-sky-100 text-[10px] font-semibold text-[#1A6FA6]">{{ locatedWorkerCount() }} visible</span>
          </div>
          <div class="p-4">
            @if (activeLocations().length > 0) {
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div class="rounded-md border border-slate-100 bg-slate-50 px-3 py-3">
                  <p class="text-[10px] font-semibold text-slate-500">Active shifts</p>
                  <p class="text-lg font-bold text-slate-950 mt-1">{{ activeLocationCount() }}</p>
                </div>
                <div class="rounded-md border border-slate-100 bg-slate-50 px-3 py-3">
                  <p class="text-[10px] font-semibold text-slate-500">Missing GPS</p>
                  <p class="text-lg font-bold text-slate-950 mt-1">{{ missingLocationCount() }}</p>
                </div>
                <div class="rounded-md border border-slate-100 bg-slate-50 px-3 py-3">
                  <p class="text-[10px] font-semibold text-slate-500">Fleet linked</p>
                  <p class="text-lg font-bold text-slate-950 mt-1">{{ fleetLocationCount() }}</p>
                </div>
              </div>
              <div class="space-y-2">
                @for (location of groupedActiveLocations().slice(0, 4); track location.user_id || location.worker_name) {
                  <a routerLink="/admin/operations/workers-control" class="flex items-center gap-3 rounded-md border border-slate-100 px-3 py-2.5 hover:bg-slate-50 transition-colors">
                    <span class="w-8 h-8 rounded-md flex items-center justify-center shrink-0" [ngClass]="hasCoordinates(location) ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'">
                      <i class="ti" [class]="hasCoordinates(location) ? 'ti-map-pin' : 'ti-map-pin-off'"></i>
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="text-[12px] font-bold text-slate-900 truncate">{{ location.worker_name || 'Active worker' }}</p>
                      <p class="text-[10px] text-slate-500 mt-0.5 truncate">{{ locationLocationLabel(location) }}</p>
                    </div>
                    <span class="h-7 px-2.5 rounded-md bg-slate-50 text-slate-500 text-[10px] font-bold shrink-0">{{ locationStatusLabel(location) }}</span>
                  </a>
                }
              </div>
            } @else {
              <div class="rounded-lg border border-slate-100 bg-slate-50 px-4 py-8 text-center">
                <p class="text-sm font-semibold text-slate-900">No active shifts</p>
                <p class="text-xs text-slate-500 mt-1">Worker location coverage starts when shifts are active.</p>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  `
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  private allJobs = signal<any[]>([]);
  dashboardScope = signal<'Today' | 'All'>('All');
  fleet = signal<FleetRow[]>([]);
  attentionItems = signal<AttentionItem[]>([]);
  urgentJobsCount = signal(0);
  unassignedJobsCount = signal(0);
  readyForOfficeCount = signal(0);
  activePlumbersCount = signal(0);
  totalPlumbers = signal(0);
  pendingQuotesCount = signal(0);
  quoteSentJobsCount = signal(0);
  pendingQuotesValue = signal(0);
  openQuoteValue = signal(0);
  fleetAlertCount = signal(0);
  activeLocations = signal<LocationSummaryItem[]>([]);
  activeLocationCount = signal(0);
  locatedWorkerCount = signal(0);
  missingLocationCount = signal(0);
  fleetLocationCount = signal(0);
  feedbackItems = signal<FeedbackSummaryItem[]>([]);
  feedbackNeedsFollowUpCount = signal(0);
  submittedFeedbackCount = signal(0);
  newFeedbackCount = signal(0);
  testimonialCandidateCount = signal(0);
  averageFeedbackRating = signal('N/A');
  fleetAttentionItems = signal<AttentionItem[]>([]);
  locationAttentionItems = signal<AttentionItem[]>([]);
  feedbackAttentionItems = signal<AttentionItem[]>([]);
  pausedAttentionItems = signal<AttentionItem[]>([]);
  safetyAttentionItems = signal<AttentionItem[]>([]);
  quotePipelineMetrics = signal<QuotePipelineMetric[]>([
    { label: 'Draft quotes', amount: 0, detail: 'No drafts loaded yet', tone: 'warning', progress: 0, route: '/admin/financials/quotes' },
    { label: 'Sent quotes', amount: 0, detail: 'Awaiting customer response', tone: 'info', progress: 0, route: '/admin/financials/quotes' },
    { label: 'Accepted quotes', amount: 0, detail: 'Ready to convert to work', tone: 'success', progress: 0, route: '/admin/financials/quotes' },
    { label: 'Follow-up due', amount: 0, detail: 'Quotes expiring or past valid date', tone: 'danger', progress: 0, route: '/admin/financials/quotes' }
  ]);

  private api = inject(ApiService);
  private fleetRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private readonly allocatedStatuses = ['Scheduled', 'In Progress', 'Paused', 'Quote Sent', 'Approved'];

  ngOnInit() {
    this.loadDashboardData();
    this.loadWorkerTotals();
    this.loadQuoteTotals();
    this.loadFleetNotifications();
    this.loadLocationSummary();
    this.loadCustomerFeedbackSummary();
    this.fleetRefreshTimer = setInterval(() => {
      this.loadFleetNotifications();
      this.loadLocationSummary();
    }, 30000);
  }

  ngOnDestroy() {
    if (this.fleetRefreshTimer) clearInterval(this.fleetRefreshTimer);
  }

  refreshDashboard() {
    this.loadDashboardData();
    this.loadWorkerTotals();
    this.loadQuoteTotals();
    this.loadFleetNotifications();
    this.loadLocationSummary();
    this.loadCustomerFeedbackSummary();
  }

  loadDashboardData() {
    this.api.getJobs().subscribe({
      next: (res: any) => {
        if (!res.success || !res.data?.jobs) return;

        this.allJobs.set(res.data.jobs);
        this.applyJobScope();
      }
    });
  }

  setDashboardScope(scope: 'Today' | 'All') {
    if (this.dashboardScope() === scope) return;
    this.dashboardScope.set(scope);
    this.applyJobScope();
  }

  private applyJobScope() {
    const scopedJobs = this.dashboardScope() === 'Today'
      ? this.allJobs().filter((job: any) => this.isTodayJob(job))
      : this.allJobs();
    const allJobs = this.allJobs();
    const unassigned = allJobs.filter((j: any) => j.status === 'Unassigned');
    const urgent = allJobs.filter((j: any) => j.priority === 'Urgent' && j.status === 'Unassigned');
    const ready = allJobs.filter((j: any) => j.status === 'Ready for Office');
    const quoteSent = allJobs.filter((j: any) => j.status === 'Quote Sent');
    const allocated = allJobs.filter((j: any) => this.allocatedStatuses.includes(j.status));
    const inProgress = scopedJobs.filter((j: any) => j.status === 'In Progress');
    const pausedLong = allJobs.filter((j: any) => j.status === 'Paused' && this.daysPaused(j) >= 30);
    const declinedSafety = allJobs.filter((j: any) => j.latest_safety_status === 'Declined' && !['Completed', 'Terminated'].includes(j.status));

    this.urgentJobsCount.set(urgent.length);
    this.unassignedJobsCount.set(unassigned.length);
    this.readyForOfficeCount.set(ready.length);
    this.quoteSentJobsCount.set(quoteSent.length);
    this.fleet.set(this.mapFleetRows(inProgress));
    this.activePlumbersCount.set(this.countAssignedPlumbers(allocated));
    this.pausedAttentionItems.set(this.buildPausedAttentionItems(pausedLong));
    this.safetyAttentionItems.set(this.buildSafetyAttentionItems(declinedSafety));
    this.refreshAttentionItems();
  }

  loadWorkerTotals() {
    this.api.getWorkers().subscribe({
      next: (res: any) => {
        const workers = res.success && res.data?.users ? res.data.users : [];
        this.totalPlumbers.set(workers.filter((worker: any) => this.isActiveWorker(worker)).length);
      }
    });
  }

  loadQuoteTotals() {
    this.api.getQuotes().subscribe({
      next: (res: any) => {
        const quotes = res.success && res.data?.quotes ? res.data.quotes : [];
        const pendingQuotes = quotes.filter((quote: any) => quote.status === 'Draft');
        const pending = pendingQuotes.length;
        this.pendingQuotesValue.set(this.sumQuoteTotals(pendingQuotes));
        this.pendingQuotesCount.set(pending);
        this.updateQuotePipelineMetrics(quotes);
        this.refreshAttentionItems();
      }
    });
  }

  loadFleetNotifications() {
    this.api.getFleetVehicles().subscribe({
      next: (res: any) => {
        const vehicles = res.success && res.data?.vehicles ? res.data.vehicles : [];
        const alerts = this.buildFleetAttentionItems(vehicles);
        this.fleetAlertCount.set(alerts.length);
        this.fleetAttentionItems.set(alerts);
        this.refreshAttentionItems();
      },
      error: () => {
        this.fleetAlertCount.set(0);
        this.fleetAttentionItems.set([]);
        this.refreshAttentionItems();
      }
    });
  }

  loadLocationSummary() {
    this.api.getActiveLocations().subscribe({
      next: (res: any) => {
        const locations = res.success && res.data?.locations ? res.data.locations : [];
        this.activeLocations.set(locations);
        this.activeLocationCount.set(locations.length);
        this.locatedWorkerCount.set(locations.filter((item: any) => this.hasCoordinates(item)).length);
        this.missingLocationCount.set(locations.filter((item: any) => !this.hasCoordinates(item)).length);
        this.fleetLocationCount.set(locations.filter((item: any) => item.rego || item.vehicle_name).length);
        this.locationAttentionItems.set(this.buildLocationAttentionItems(locations));
        this.refreshAttentionItems();
      },
      error: () => {
        this.activeLocations.set([]);
        this.activeLocationCount.set(0);
        this.locatedWorkerCount.set(0);
        this.missingLocationCount.set(0);
        this.fleetLocationCount.set(0);
        this.locationAttentionItems.set([]);
        this.refreshAttentionItems();
      }
    });
  }

  loadCustomerFeedbackSummary() {
    this.api.getCustomerFeedback().subscribe({
      next: (res: any) => {
        const feedback = res.success && res.data?.items ? res.data.items : [];
        this.feedbackItems.set(feedback);
        const submitted = feedback.filter((item: any) => item.status === 'Submitted');
        const followUp = feedback.filter((item: any) => this.isFeedbackFollowUp(item));
        const testimonials = feedback.filter((item: any) => this.hasTestimonialPermission(item) && item.status !== 'Approved Testimonial');
        this.submittedFeedbackCount.set(submitted.length);
        this.newFeedbackCount.set(submitted.length);
        this.feedbackNeedsFollowUpCount.set(followUp.length);
        this.testimonialCandidateCount.set(testimonials.length);
        this.averageFeedbackRating.set(this.calculateAverageFeedbackRating(feedback));
        this.feedbackAttentionItems.set(this.buildFeedbackAttentionItems(feedback));
        this.refreshAttentionItems();
      },
      error: () => {
        this.feedbackItems.set([]);
        this.submittedFeedbackCount.set(0);
        this.newFeedbackCount.set(0);
        this.feedbackNeedsFollowUpCount.set(0);
        this.testimonialCandidateCount.set(0);
        this.averageFeedbackRating.set('N/A');
        this.feedbackAttentionItems.set([]);
        this.refreshAttentionItems();
      }
    });
  }

  mapFleetRows(inProgressJobs: any[]): FleetRow[] {
    return inProgressJobs.flatMap((job: any) => {
      const workers = String(job.assigned_workers || 'Unassigned Worker')
        .split(',')
        .map(worker => worker.trim())
        .filter(Boolean);

      return workers.map(name => ({
        name,
        status: job.status || 'In Progress',
        job: job.job_id || `#${job.id}`,
        address: job.address || job.job_address || '',
        duration: this.durationLabel(job.started_at || job.created_at),
        action: job.status === 'Unassigned' ? 'Dispatch' : 'View',
        route: ['/admin/dispatch/job', job.id]
      }));
    });
  }

  countAssignedPlumbers(jobs: any[]): number {
    const plumbers = new Set<string>();
    jobs.forEach((job: any) => {
      String(job.assigned_workers || '')
        .split(',')
        .map(worker => worker.trim())
        .filter(Boolean)
        .forEach(worker => plumbers.add(worker));
    });
    return plumbers.size;
  }

  refreshAttentionItems() {
    this.attentionItems.set(this.buildAttentionItems(
      this.urgentJobsCount(),
      this.readyForOfficeCount(),
      this.pendingQuotesCount(),
      this.fleetAttentionItems(),
      [
        ...this.feedbackAttentionItems(),
        ...this.locationAttentionItems(),
        ...this.pausedAttentionItems(),
        ...this.safetyAttentionItems()
      ]
    ));
  }

  buildAttentionItems(urgentCount: number, readyCount: number, pendingQuotes: number, fleetItems: AttentionItem[] = [], pausedItems: AttentionItem[] = []): AttentionItem[] {
    const items: AttentionItem[] = [];
    items.push(...fleetItems);
    items.push(...pausedItems);
    if (urgentCount > 0) {
      items.push({
        label: `${urgentCount} urgent job${urgentCount === 1 ? '' : 's'} unassigned`,
        detail: 'Allocate these before standard queue work.',
        tone: 'danger',
        route: '/admin/dispatch/unassigned',
        action: 'Dispatch'
      });
    }
    if (readyCount > 0) {
      items.push({
        label: `${readyCount} job${readyCount === 1 ? '' : 's'} ready for office`,
        detail: 'Review labour, materials, photos, and closeout notes.',
        tone: 'warning',
        route: '/admin/dispatch/ready',
        action: 'Review'
      });
    }
    if (pendingQuotes > 0) {
      items.push({
        label: `${pendingQuotes} quote${pendingQuotes === 1 ? '' : 's'} awaiting action`,
        detail: 'Follow up draft and sent quotations.',
        tone: 'info',
        route: '/admin/financials/quotes',
        action: 'Chase'
      });
    }
    return items;
  }

  buildPausedAttentionItems(jobs: any[]): AttentionItem[] {
    return jobs.slice(0, 4).map(job => {
      const days = this.daysPaused(job);
      return {
        label: `${job.id} paused ${days} days`,
        detail: `${job.customer_name || 'Job'} has been paused for one month or more.`,
        tone: 'warning',
        route: `/admin/dispatch/job/${job.id}`,
        action: 'View'
      };
    });
  }

  buildSafetyAttentionItems(jobs: any[]): AttentionItem[] {
    return jobs.slice(0, 4).map(job => ({
      label: `${job.id} safety declined`,
      detail: `${job.customer_name || 'Job'} has a safety concern from the field team.`,
      tone: 'danger',
      route: `/admin/dispatch/job/${job.id}`,
      action: 'View'
    }));
  }

  buildFeedbackAttentionItems(feedback: FeedbackSummaryItem[]): AttentionItem[] {
    return feedback
      .filter(item => this.isFeedbackFollowUp(item) || item.status === 'Submitted')
      .slice(0, 4)
      .map(item => ({
        label: this.isFeedbackFollowUp(item)
          ? `${item.reference_id || 'Feedback'} needs follow-up`
          : `${item.reference_id || 'Feedback'} received`,
        detail: this.shortText(item.comments || item.customer_name || 'Review customer feedback.'),
        tone: this.isFeedbackFollowUp(item) ? 'danger' : 'info',
        route: this.feedbackRoute(item),
        action: this.isFeedbackFollowUp(item) ? 'Call' : 'Review'
      }));
  }

  buildLocationAttentionItems(locations: LocationSummaryItem[]): AttentionItem[] {
    const missingCount = locations.filter(item => !this.hasCoordinates(item)).length;
    if (!missingCount) return [];
    return [{
      label: `${missingCount} active plumber${missingCount === 1 ? '' : 's'} without GPS`,
      detail: 'Check mobile location permission for active shift visibility.',
      tone: 'warning',
      route: '/admin/operations/workers-control',
      action: 'Control'
    }];
  }

  buildFleetAttentionItems(vehicles: any[]): AttentionItem[] {
    const alerts: AttentionItem[] = [];
    vehicles.forEach(vehicle => {
      const plate = vehicle.rego || 'Vehicle';
      const model = vehicle.vehicle || 'Fleet vehicle';
      const complianceType = vehicle.complianceType || 'WOF';
      const complianceDays = this.daysUntil(vehicle.wof);
      if (complianceDays !== null && complianceDays < 0) {
        alerts.push({
          label: `${plate} ${complianceType} overdue`,
          detail: `${model} expired ${Math.abs(complianceDays)} day${Math.abs(complianceDays) === 1 ? '' : 's'} ago.`,
          tone: 'danger',
          route: '/admin/operations/fleet'
        });
      } else if (complianceDays !== null && complianceDays <= 30) {
        alerts.push({
          label: `${plate} ${complianceType} due soon`,
          detail: `${model} expires in ${complianceDays} day${complianceDays === 1 ? '' : 's'}.`,
          tone: 'warning',
          route: '/admin/operations/fleet'
        });
      }

      const serviceRemaining = this.kmRemaining(vehicle.odo, vehicle.service);
      if (serviceRemaining !== null && serviceRemaining < 0) {
        alerts.push({
          label: `${plate} service overdue`,
          detail: `${model} is ${Math.abs(serviceRemaining).toLocaleString()} km past service due.`,
          tone: 'danger',
          route: '/admin/operations/fleet'
        });
      } else if (serviceRemaining !== null && serviceRemaining <= 2000) {
        alerts.push({
          label: `${plate} service due soon`,
          detail: `${model} has ${serviceRemaining.toLocaleString()} km until service.`,
          tone: 'warning',
          route: '/admin/operations/fleet'
        });
      }

      const rucRemaining = this.kmRemaining(vehicle.odo, vehicle.ruc);
      if (rucRemaining !== null && rucRemaining < 0) {
        alerts.push({
          label: `${plate} RUC overdue`,
          detail: `${model} is ${Math.abs(rucRemaining).toLocaleString()} km past paid RUC.`,
          tone: 'danger',
          route: '/admin/operations/fleet'
        });
      } else if (rucRemaining !== null && rucRemaining <= 1000) {
        alerts.push({
          label: `${plate} RUC due soon`,
          detail: `${model} has ${rucRemaining.toLocaleString()} km remaining on RUC.`,
          tone: 'warning',
          route: '/admin/operations/fleet'
        });
      }
    });

    return alerts
      .sort((a, b) => this.alertToneRank(a.tone) - this.alertToneRank(b.tone))
      .slice(0, 6);
  }

  statusBadgeClass(status: string): string {
    return jobStatusClass(status);
  }

  attentionBorderClass(tone: AttentionItem['tone']): string {
    if (tone === 'danger') return 'border-rose-100 hover:border-rose-200';
    if (tone === 'warning') return 'border-amber-100 hover:border-amber-200';
    return 'border-sky-100 hover:border-sky-200';
  }

  attentionIconClass(tone: AttentionItem['tone']): string {
    if (tone === 'danger') return 'bg-rose-50 text-rose-600';
    if (tone === 'warning') return 'bg-amber-50 text-amber-600';
    return 'bg-sky-50 text-[#1A6FA6]';
  }

  attentionIconName(item: AttentionItem): string {
    const text = `${item.label} ${item.detail}`.toLowerCase();
    if (text.includes('wof') || text.includes('service') || text.includes('ruc') || text.includes('fleet')) return 'truck';
    if (text.includes('feedback') || text.includes('customer recovery')) return 'message-star';
    if (text.includes('gps') || text.includes('location')) return 'map-pin';
    if (text.includes('ready for office') || item.action === 'Review') return 'clipboard-check';
    if (text.includes('quote') || item.action === 'Chase') return 'file-dollar';
    if (text.includes('safety')) return 'shield-exclamation';
    if (text.includes('paused')) return 'player-pause';
    return 'alert-triangle';
  }

  attentionActionClass(tone: AttentionItem['tone']): string {
    if (tone === 'danger') return 'bg-rose-50 text-rose-700';
    if (tone === 'warning') return 'bg-amber-50 text-amber-700';
    return 'bg-sky-50 text-[#1A6FA6]';
  }

  quoteToneClass(tone: QuotePipelineMetric['tone']): string {
    if (tone === 'success') return 'text-emerald-600';
    if (tone === 'danger') return 'text-rose-600';
    if (tone === 'warning') return 'text-amber-600';
    return 'text-[#1A6FA6]';
  }

  quoteBarClass(tone: QuotePipelineMetric['tone']): string {
    if (tone === 'success') return 'bg-emerald-500';
    if (tone === 'danger') return 'bg-rose-500';
    if (tone === 'warning') return 'bg-amber-500';
    return 'bg-[#1A6FA6]';
  }

  private updateQuotePipelineMetrics(quotes: any[]) {
    const draftQuotes = quotes.filter((quote: any) => quote.status === 'Draft');
    const sentQuotes = quotes.filter((quote: any) => quote.status === 'Sent');
    const acceptedQuotes = quotes.filter((quote: any) => quote.status === 'Accepted');
    const followUpQuotes = sentQuotes.filter((quote: any) => this.isQuoteFollowUpDue(quote));
    const draftValue = this.sumQuoteTotals(draftQuotes);
    const sentValue = this.sumQuoteTotals(sentQuotes);
    const acceptedValue = this.sumQuoteTotals(acceptedQuotes);
    const followUpValue = this.sumQuoteTotals(followUpQuotes);
    const maxAmount = Math.max(draftValue, sentValue, acceptedValue, followUpValue, 1);

    this.openQuoteValue.set(draftValue + sentValue);
    this.quotePipelineMetrics.set([
      {
        label: 'Draft quotes',
        amount: draftValue,
        detail: `${draftQuotes.length} draft${draftQuotes.length === 1 ? '' : 's'} being prepared`,
        tone: 'warning',
        progress: this.percentOf(draftValue, maxAmount),
        route: '/admin/financials/quotes'
      },
      {
        label: 'Sent quotes',
        amount: sentValue,
        detail: `${sentQuotes.length} sent quote${sentQuotes.length === 1 ? '' : 's'} awaiting response`,
        tone: 'info',
        progress: this.percentOf(sentValue, maxAmount),
        route: '/admin/financials/quotes'
      },
      {
        label: 'Accepted quotes',
        amount: acceptedValue,
        detail: `${acceptedQuotes.length} accepted quote${acceptedQuotes.length === 1 ? '' : 's'} ready for work`,
        tone: 'success',
        progress: this.percentOf(acceptedValue, maxAmount),
        route: '/admin/financials/quotes'
      },
      {
        label: 'Follow-up due',
        amount: followUpValue,
        detail: `${followUpQuotes.length} sent quote${followUpQuotes.length === 1 ? '' : 's'} near or past valid date`,
        tone: 'danger',
        progress: this.percentOf(followUpValue, maxAmount),
        route: '/admin/financials/quotes'
      }
    ]);
  }

  feedbackRoute(item: FeedbackSummaryItem): string {
    return '/admin/customer-feedback';
  }

  feedbackRatingClass(item: FeedbackSummaryItem): string {
    const rating = Number(item.rating || 0);
    if (rating > 0 && rating <= 2) return 'bg-rose-50 text-rose-700';
    if (rating === 3) return 'bg-amber-50 text-amber-700';
    if (rating >= 4) return 'bg-emerald-50 text-emerald-700';
    return 'bg-slate-50 text-slate-500';
  }

  feedbackStatusClass(status: string): string {
    if (status === 'Needs Follow-up') return 'bg-rose-50 text-rose-700';
    if (status === 'Submitted') return 'bg-sky-50 text-[#1A6FA6]';
    if (status === 'Resolved' || status === 'Approved Testimonial') return 'bg-emerald-50 text-emerald-700';
    return 'bg-amber-50 text-amber-700';
  }

  hasCoordinates(location: LocationSummaryItem): boolean {
    return location.latitude !== null
      && location.latitude !== undefined
      && location.latitude !== ''
      && location.longitude !== null
      && location.longitude !== undefined
      && location.longitude !== '';
  }

  locationLocationLabel(location: LocationSummaryItem): string {
    if (Number(location.active_count || 0) > 1) {
      const details = [
        `${location.active_count} active rows`,
        Number(location.fleet_count || 0) > 0 ? `${location.fleet_count} fleet linked` : '',
        Number(location.missing_count || 0) > 0 ? `${location.missing_count} missing GPS` : ''
      ].filter(Boolean);
      return details.join(' - ');
    }
    const vehicle = location.rego || location.vehicle_name;
    const job = location.current_job || location.vehicle_location;
    if (vehicle && job) return `${vehicle} - ${job}`;
    if (vehicle) return vehicle;
    if (job) return job;
    return this.hasCoordinates(location) ? 'Location available' : 'Location permission pending';
  }

  locationTimeLabel(value: string): string {
    if (!value) return 'No GPS';
    return this.durationLabel(value) + ' ago';
  }

  locationStatusLabel(location: LocationSummaryItem): string {
    if (Number(location.active_count || 0) > 1) return `${location.active_count} rows`;
    return this.locationTimeLabel(location.location_updated_at);
  }

  groupedActiveLocations(): LocationSummaryItem[] {
    const groups = new Map<string, LocationSummaryItem>();
    this.activeLocations().forEach(location => {
      const key = String(location.user_id || location.worker_name || '').trim().toLowerCase();
      if (!key) return;
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, {
          ...location,
          active_count: 1,
          missing_count: this.hasCoordinates(location) ? 0 : 1,
          fleet_count: location.rego || location.vehicle_name ? 1 : 0
        });
        return;
      }

      existing.active_count = Number(existing.active_count || 1) + 1;
      existing.missing_count = Number(existing.missing_count || 0) + (this.hasCoordinates(location) ? 0 : 1);
      existing.fleet_count = Number(existing.fleet_count || 0) + (location.rego || location.vehicle_name ? 1 : 0);
      if (!this.hasCoordinates(existing) && this.hasCoordinates(location)) {
        existing.latitude = location.latitude;
        existing.longitude = location.longitude;
        existing.location_updated_at = location.location_updated_at;
      }
      if (!existing.rego && location.rego) existing.rego = location.rego;
      if (!existing.vehicle_name && location.vehicle_name) existing.vehicle_name = location.vehicle_name;
      if (!existing.current_job && location.current_job) existing.current_job = location.current_job;
      if (!existing.vehicle_location && location.vehicle_location) existing.vehicle_location = location.vehicle_location;
    });
    return Array.from(groups.values());
  }

  shortText(value: string, max = 74): string {
    const text = String(value || '').trim();
    if (text.length <= max) return text;
    return `${text.slice(0, max - 3)}...`;
  }

  private isFeedbackFollowUp(item: FeedbackSummaryItem): boolean {
    if (this.isFeedbackClosed(item.status)) return false;
    const rating = Number(item.rating || 0);
    return item.status === 'Needs Follow-up' || (rating > 0 && rating <= 3);
  }

  private isFeedbackClosed(status: string): boolean {
    return status === 'Resolved' || status === 'Approved Testimonial';
  }

  private hasTestimonialPermission(item: FeedbackSummaryItem): boolean {
    return item.testimonial_permission === true || Number(item.testimonial_permission) === 1 || item.testimonial_permission === '1';
  }

  private calculateAverageFeedbackRating(feedback: FeedbackSummaryItem[]): string {
    const ratings = feedback
      .map(item => Number(item.rating || 0))
      .filter(rating => Number.isFinite(rating) && rating > 0);
    if (!ratings.length) return 'N/A';
    const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
    return `${average.toFixed(1)} / 5`;
  }

  private sumQuoteTotals(quotes: any[]): number {
    return quotes.reduce((sum: number, quote: any) => sum + this.moneyNumber(quote.total), 0);
  }

  private isQuoteFollowUpDue(quote: any): boolean {
    const validUntil = this.parseDate(quote.valid_until);
    if (!validUntil) return false;
    const days = Math.ceil((validUntil.getTime() - Date.now()) / 86400000);
    return days <= 3;
  }

  private durationLabel(value: string): string {
    const start = this.parseDate(value);
    if (!start) return 'Today';
    const minutes = Math.max(0, Math.floor((Date.now() - start.getTime()) / 60000));
    if (minutes < 60) return `${minutes || 1}m`;
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
  }

  private moneyNumber(value: any): number {
    const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private percentOf(value: number, max: number): number {
    return Math.max(6, Math.min(100, Math.round((value / max) * 100)));
  }

  private daysUntil(value: string): number | null {
    const target = this.parseDate(value);
    if (!target) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target.getTime() - today.getTime()) / 86400000);
  }

  private daysPaused(job: any): number {
    const paused = this.parseDate(job?.pause_started_at || job?.paused_at);
    if (!paused) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    paused.setHours(0, 0, 0, 0);
    return Math.max(0, Math.floor((today.getTime() - paused.getTime()) / 86400000));
  }

  private isTodayJob(job: any): boolean {
    if (['In Progress', 'Paused'].includes(job?.status)) return true;
    return [
      job?.scheduled_date,
      job?.started_at,
      job?.completed_at,
      job?.paused_at,
      job?.pause_started_at,
      job?.created_at
    ].some(value => this.isTodayDate(value));
  }

  private isActiveWorker(worker: any): boolean {
    return Number(worker?.is_active) === 1 || worker?.is_active === true;
  }

  private isTodayDate(value: string): boolean {
    const date = this.parseDate(value);
    if (!date) return false;
    const today = new Date();
    return date.getFullYear() === today.getFullYear()
      && date.getMonth() === today.getMonth()
      && date.getDate() === today.getDate();
  }

  private parseDate(value: string): Date | null {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private kmRemaining(currentValue: string, targetValue: string): number | null {
    const current = this.kmNumber(currentValue);
    const target = this.kmNumber(targetValue);
    if (current === null || target === null) return null;
    return target - current;
  }

  private kmNumber(value: string): number | null {
    const numeric = String(value || '').replace(/[^0-9]/g, '');
    if (!numeric) return null;
    return Number(numeric);
  }

  private alertToneRank(tone: AttentionItem['tone']): number {
    if (tone === 'danger') return 0;
    if (tone === 'warning') return 1;
    return 2;
  }
}
